import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, Gauge, History, Scale, ShieldCheck, GraduationCap } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useMode } from "@/context/ModeContext";
import { PredictionScore } from "@/components/PredictionScore";
import { PredictionLedger } from "@/components/PredictionLedger";
import { Locked } from "@/components/Locked";
import { InvestorSummary } from "@/components/quality/Evidence";
import { TransparencyEvidence } from "@/components/site/TransparencyEvidence";
import { TrackRecordInner } from "@/pages/TrackRecord";
import { QualityBody, QualityAdminCards } from "@/pages/Quality";

const TABS = [
  { v: "summary", label: "Summary", icon: GraduationCap, tid: "perf-tab-summary" },
  { v: "score", label: "Accuracy score & ledger", icon: Gauge, tid: "perf-tab-score" },
  { v: "signals", label: "Signal track record", icon: History, tid: "perf-tab-signals" },
  { v: "quality", label: "Prediction quality", icon: BarChart3, tid: "perf-tab-quality" },
  { v: "transparency", label: "Transparency & methodology", icon: Scale, tid: "perf-tab-transparency" },
];

// One home for every performance, prediction and transparency figure. Beginner mode = short plain-language summary;
// Advanced mode = the full statistics and methodology, in tabs, on this same page.
export default function Performance() {
  const { beginner, setMode } = useMode();
  const [params, setParams] = useSearchParams();
  const wanted = params.get("tab");
  const tab = TABS.some((t) => t.v === wanted) ? wanted : "summary";
  const setTab = (t) => { const p = new URLSearchParams(params); p.set("tab", t); setParams(p, { replace: true }); };
  useEffect(() => { if (window.location.hash) document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }); }, []);

  return (
    <div className="p-4 md:p-6 max-w-[1500px] mx-auto space-y-5" data-testid="performance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-emerald-300" /> Performance & Transparency</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-3xl">The single place for how EV-IDEX predictions have actually done: the graded accuracy score, the sealed prediction ledger, every past signal, the independent quality audit and the public transparency evidence. Nothing here is edited by hand.</p>
        </div>
        <div className="text-[11px] text-slate-500 flex items-center gap-2" data-testid="perf-mode-note">
          {beginner ? "Beginner mode · plain-language summary" : "Advanced mode · full statistics"}
          <button type="button" onClick={() => setMode(beginner ? "advanced" : "beginner")} className="rounded-full border border-slate-800 px-3 py-1 text-xs text-cyan-200 hover:border-cyan-800" data-testid="perf-mode-toggle">
            {beginner ? "Show detailed statistics" : "Back to plain summary"}
          </button>
        </div>
      </div>

      {beginner ? (
        <div className="space-y-4" data-testid="perf-beginner">
          <section className="apx-card p-5 space-y-3" data-testid="perf-plain-summary">
            <h2 className="font-display font-semibold text-lg">In plain language</h2>
            <ul className="text-sm text-slate-300 leading-relaxed list-disc list-inside space-y-1.5">
              <li><strong className="text-slate-100">What is measured:</strong> every prediction EV-IDEX publishes is sealed when it is made, then graded later by fixed rules against the real price. Nobody can edit a result afterwards.</li>
              <li><strong className="text-slate-100">What the score means:</strong> the accuracy score counts only Bullish/Bearish calls that could be right or wrong. "Wait" and "Neutral" calls are reported separately and never inflate it.</li>
              <li><strong className="text-slate-100">Small samples:</strong> until enough calls have been graded, figures are labelled <em>preliminary</em> — they are not evidence of an edge, and they can swing a lot.</li>
              <li><strong className="text-slate-100">Three kinds of evidence, kept apart:</strong> backtested (simulated on past data, can flatter), paper-traded (Model v2's research ledger, never shown as advice) and live (the public ledger you can verify).</li>
              <li><strong className="text-slate-100">Not advice:</strong> accuracy is a hit rate, not profit. Past results never guarantee future ones.</li>
            </ul>
            <div className="text-xs text-slate-500">Want the numbers? Use <button type="button" onClick={() => setMode("advanced")} className="text-cyan-300 hover:underline" data-testid="perf-plain-to-advanced">Show detailed statistics</button> — the accuracy dials, the full ledger, every past signal, the quality audit and the methodology open on this same page.</div>
          </section>
          <section className="apx-card p-5 space-y-3" data-testid="perf-auto-summary">
            <div><h2 className="font-display font-semibold text-lg">What has been verified so far</h2><p className="text-xs text-slate-400 mt-1">Written automatically from the sealed ledgers. Every statement carries its sample size.</p></div>
            <InvestorSummary />
          </section>
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-900 border border-slate-800 w-full flex-wrap h-auto justify-start gap-1 p-1" data-testid="perf-tabs">
            {TABS.filter((t) => t.v !== "summary").map(({ v, label, icon: Icon, tid }) => (
              <TabsTrigger key={v} value={v} data-testid={tid} className="gap-1.5 text-xs"><Icon className="w-3.5 h-3.5" /> {label}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="summary" className="mt-4" data-testid="perf-advanced-summary">
            <div className="apx-card p-5 space-y-3"><h2 className="font-display font-semibold text-lg">What has been verified so far</h2><InvestorSummary /></div>
            <PredictionScore />
          </TabsContent>
          <TabsContent value="score" className="mt-4 space-y-5" data-testid="perf-score">
            <PredictionScore />
            <PredictionLedger />
          </TabsContent>
          <TabsContent value="signals" className="mt-4 -mx-4 md:-mx-6" data-testid="perf-signals">
            <Locked feature="track_record" title="Signal track record is a Pro feature"
              desc="See every past BUY / WAIT / AVOID call with its outcome at 24h, 7d and 30d, momentum columns and the full setup timeline. Your existing history is preserved and unlocks instantly on Pro.">
              <TrackRecordInner />
            </Locked>
          </TabsContent>
          <TabsContent value="quality" className="mt-4 -mx-4 md:-mx-6" data-testid="perf-quality">
            <QualityBody />
          </TabsContent>
          <TabsContent value="transparency" className="mt-4" data-testid="perf-transparency">
            <TransparencyEvidence signedIn detailLink={{ to: "/performance?tab=quality", label: "Prediction quality" }} />
          </TabsContent>
        </Tabs>
      )}

      <QualityAdminCards />
    </div>
  );
}
